import {useParams} from 'next/navigation'
import {useMemo} from 'react'

import {useQuery} from '@tanstack/react-query'
import {isWithinInterval} from 'date-fns'

import useInsuranceMyAssetQuery from '$hooks/insurance/useInsuranceMyAssetQuery'
import {getFreeInsuranceDetailQuery, getRegionsQuery} from '$queries/insurance/myQuery'
import {displaySpace} from '$utils/common'
import {convertToDate, ServerDate} from '$utils/date'
import {formatAmount} from '$utils/formatAmount'

const useFreeInsuranceDetail = () => {
    const {id: rCode, insuranceId: assetId} = useParams<{id: string; insuranceId: string}>()

    const {data: registrationData} = useInsuranceMyAssetQuery()

    const {data: freeInsurance} = useQuery({
        ...getFreeInsuranceDetailQuery.query({
            params: {assetId: +assetId},
        }),
        enabled: !!assetId,
    })

    const 보장기간 = useMemo(
        () =>
            !!freeInsurance?.insuStDt &&
            !!freeInsurance?.insuEdDt && {
                start: convertToDate(freeInsurance?.insuStDt),
                end: convertToDate(freeInsurance?.insuEdDt),
            },
        [freeInsurance?.insuEdDt, freeInsurance?.insuStDt],
    )

    const {data: regionsData = []} = useQuery({
        ...getRegionsQuery.query(),
    })

    const regionName = useMemo(() => {
        const region = regionsData?.find(({rcode}) => rcode === rCode.slice(0, 2))
        // 상위도, 특별시등에서 이미 같아버리는 경우
        // 예: 세종특별시
        if (region?.rcode === rCode) {
            return region.doName
        }

        if ((region?.regions || []).length > 0) {
            const subRegion = region?.regions.find((r) => r.rcode === rCode)
            return `${subRegion?.doName} ${subRegion?.siName}`
        }

        return ''
    }, [rCode, regionsData])

    return useMemo(
        () => ({
            hasValidInsurances: !!registrationData?.isMydataInsuranceConnected,
            id: freeInsurance?.freeInsuSeqno,
            계약자: freeInsurance?.contractor,
            name: displaySpace(freeInsurance?.prodName),
            freeInsuCategory: freeInsurance?.category,
            websiteLink: freeInsurance?.websiteLink,
            tel: freeInsurance?.phoneNum,
            보장기간,
            보험공제사: freeInsurance?.insuMutualAgency,
            계약상태: 보장기간 && isWithinInterval(ServerDate(), 보장기간),
            보장내용: freeInsurance?.guarantees.map(({benefitAmt, benefitContent}) => ({
                보장: benefitContent || '',
                보장금액: formatAmount(benefitAmt, {type: '만'}),
            })),
            regionName,
        }),
        [
            freeInsurance?.category,
            freeInsurance?.contractor,
            freeInsurance?.freeInsuSeqno,
            freeInsurance?.guarantees,
            freeInsurance?.insuMutualAgency,
            freeInsurance?.phoneNum,
            freeInsurance?.prodName,
            freeInsurance?.websiteLink,
            regionName,
            registrationData?.isMydataInsuranceConnected,
            보장기간,
        ],
    )
}

export default useFreeInsuranceDetail
